import React, { useState } from 'react';
import { Box, Button, IconButton, Menu, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { capitalize } from 'lodash';
import toast from 'react-hot-toast';
import ListItemCard, { ListItemCardProps } from '../../../components/ListItemCard';
import { ReactComponent as MoreOptionsIcon } from '../../../assets/svg/more-options.svg';
import { ProductTestingModal } from './ProductTestingModal';
import { formatFilmDay } from '../../../utils/filmWindow';
import { getProDecision } from '../../videoProduction/helper';
import { downloadUserSow } from '../../../api/endpoints';
import { useUser } from '../../../hooks/useUser';
import { openPdfFromApi } from '../../../utils/openPdfFromApi';


// Expert-facing twins of the internal pills in ExpertOptStatusBar — same states
// (opted in / out / awaiting / filming) seen from the expert's side, so they use
// the same families and must stay in sync. Light keeps the exact original
// pastels; dark swaps in a translucent wash of the same hue plus a light ink,
// because a pastel fill on the dark card is a bright blob with ~2:1 text.
// chipSx is spread into an MUI sx prop (ListItemCard), so the nested
// [data-theme="dark"] selector resolves; a plain `style` could not do this.
const dark = (color: string, backgroundColor: string) => ({
  '[data-theme="dark"] &': { color, backgroundColor },
});
const CHIP_GREEN = {
  backgroundColor: '#E7F2E8',
  color: '#2E7D32',
  ...dark('var(--up)', 'var(--up-bg)'),
};
const CHIP_RED = {
  backgroundColor: '#FBEAEA',
  color: '#C62828',
  ...dark('var(--down)', 'var(--down-bg)'),
};
const CHIP_AMBER = {
  backgroundColor: '#FDF3E2',
  color: '#9A6B00',
  // no amber token exists, and Tailwind-style opacity on a var() is not an
  // option in sx either — so the wash is an explicit rgba of the same hue.
  ...dark('#E0BE73', 'rgba(122,91,18,.30)'),
};
const CHIP_GOLD = {
  backgroundColor: '#F2EBE2',
  color: '#8A6A2F',
  ...dark('var(--accent-text)', 'var(--tint)'),
};
const CHIP_SLATE = {
  backgroundColor: '#EEF2F6',
  color: '#3B4A5A',
  ...dark('#9CC2E8', 'rgba(31,78,121,.35)'),
};

interface Props {
  project: any;
  /** Opens the SOW signing modal (held at page level) for this envelope. */
  onSignSow?: (envelopeId: string) => void;
}

const startCase = (text: string) =>
  text.replace(/\b\w+(?:'\w+)?\b/g, (word: string) => capitalize(word));

const ExpertProjectCard: React.FC<Props> = ({ project, onSignSow }) => {
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [showProductTesting, setShowProductTesting] = useState(false);
  const { user } = useUser();

  const products = project?.products as Array<{ image?: string; title?: string; desireid?: number }> | undefined;
  const productWithImage = products?.find((p) => p.image) || project?.product;
  const primaryProduct = productWithImage?.image ? productWithImage : (products && products.length > 0 ? products[0] : project?.product);
  const productCount = products && products.length > 1 ? products.length : undefined;
  const productTitles = products && products.length > 0
    ? products.map((p) => p.title).filter(Boolean).join(', ')
    : project?.product?.title || '';

  // One card per SELECTION PROCESS: the projects on it share the product,
  // brief, film date and this expert's single opt-in, so the API lists the
  // process once (lowest-id project) with its siblings as process_projects.
  const processProjects: Array<{ id: number; title?: string; content_type?: { name?: string } | null }> =
    Array.isArray(project.process_projects) && project.process_projects.length > 0
      ? project.process_projects
      : [{ id: project.id, title: project.title, content_type: project.content_type }];
  const multiProject = processProjects.length > 1;
  const cardTitle = multiProject
    ? processProjects.map((p) => p.title || 'Untitled Project').join(' · ')
    : project?.title || 'Untitled Project';
  const contentTypes = Array.from(
    new Set(
      processProjects
        .map((p) => p.content_type?.name || '')
        .filter(Boolean)
    )
  );
  const myPro = project.project_pros?.[0]?.brand_projects_pros;
  const hasSubmitted = project.project_pros?.some(
    (pp: any) => pp.brand_projects_pros?.confirmation_status === 'submitted'
  );
  // Confirmed experts get the menu; experts with existing feedback keep it
  // even after casting resets internal_confirmed (mirrors the API gate).
  const canGiveProductFeedback =
    (myPro?.interested === true && myPro?.internal_confirmed === true) ||
    myPro?.has_product_testing === true;

  // The expert's SOW envelope for this selection (shared across every
  // sibling project on the same brand_order_pros).
  const sowStatus = myPro?.expert_signing_status;
  const sowEnvelopeId = myPro?.expert_envelope_id;
  // Fully executed only once the company has countersigned — until then the
  // chip must not present a one-sided contract as "signed".
  const sowFullyExecuted = myPro?.internal_signing_status === 'completed';

  const handleViewSignedSow = () => {
    if (!user?.userid || !sowEnvelopeId) return;
    openPdfFromApi(downloadUserSow(user.userid, sowEnvelopeId)).catch(() =>
      toast.error("Couldn't open your Statement of Work. Try again.")
    );
  };

  const chips: ListItemCardProps['chips'] = [];

  if (multiProject) {
    chips.push({
      label: `${processProjects.length} videos · one response`,
      chipSx: { ...CHIP_SLATE, fontWeight: 700 },
    });
  }
  contentTypes.forEach((contentType) => {
    chips.push({ label: startCase(contentType.replace(/_/i, ' ')) });
  });

  const filmDate = formatFilmDay(project.film_date);
  const decision = getProDecision(myPro);
  const optedOut = decision === 'opted_out';
  // Opt-ins close once the selection is in pre-production (API
  // opt_ins_closed). Experts who already opted in are unaffected; everyone
  // else sees the window closed instead of an Opt In call to action.
  const optInsClosed = project.opt_ins_closed === true && decision !== 'opted_in';
  let responseButtonLabel = 'Opt In / Out';
  if (hasSubmitted) responseButtonLabel = 'View Response';
  else if (optInsClosed) responseButtonLabel = 'View Details';
  if (optInsClosed) {
    chips.push({
      label: 'Opt-ins closed',
      chipSx: { ...CHIP_RED, fontWeight: 700 },
    });
  }
  if (filmDate) {
    const confirmed = myPro?.user_confirmed;
    const lastDay = formatFilmDay(project.film_date_end);
    const multiDay = !!lastDay && lastDay !== filmDate;
    chips.push({
      label: multiDay ? `Filming from ${filmDate}` : `Filming ${filmDate}`,
      chipSx: { ...CHIP_GOLD, fontWeight: 700 },
    });
    if (!optedOut && confirmed === true) {
      chips.push({
        label: 'Date confirmed',
        chipSx: { ...CHIP_GREEN, fontWeight: 700 },
      });
    } else if (!optedOut && confirmed === false) {
      chips.push({
        label: 'Date declined',
        chipSx: { ...CHIP_RED, fontWeight: 700 },
      });
    } else if (!optedOut) {
      chips.push({
        label: 'Confirm this date',
        chipSx: { ...CHIP_AMBER, fontWeight: 700 },
      });
    }
  }
  if (sowStatus === 'completed' && sowEnvelopeId) {
    chips.push({
      // Both states open the download — the server serves the right artifact
      // for each (expert-signed copy vs fully executed with countersignature).
      label: sowFullyExecuted ? 'SOW Signed' : 'SOW Pending Countersign',
      onClick: handleViewSignedSow,
      chipSx: { cursor: 'pointer' },
    });
  }

  const card = (
    <ListItemCard
      image={primaryProduct?.image}
      imageCount={productCount}
      title={cardTitle}
      subtitle={productTitles || undefined}
      associationIds=""
      brands={project.brands}
      chips={chips}
      rightContent={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: 1,
            minWidth: 0,
          }}
        >
          {sowStatus === 'sent' && sowEnvelopeId && onSignSow && (
            <Button
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                onSignSow(sowEnvelopeId);
              }}
              sx={{
                textTransform: 'none',
                borderRadius: '12px',
                px: 3,
                py: 1,
                fontFamily: 'CircularStd',
                fontSize: '14px',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              Sign SOW
            </Button>
          )}
          <Button
            variant="contained"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/expert/project/${project.id}/confirmation-details`);
            }}
            sx={{
              textTransform: 'none',
              borderRadius: '12px',
              px: 3,
              py: 1,
              backgroundColor: 'rgb(165, 132, 80)',
              fontFamily: 'CircularStd',
              fontSize: '14px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              '&:hover': { backgroundColor: 'rgb(140, 112, 65)' },
            }}
          >
            {responseButtonLabel}
          </Button>
          {canGiveProductFeedback && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setMenuAnchor(e.currentTarget);
              }}
              sx={{ flexShrink: 0 }}
            >
              <MoreOptionsIcon />
            </IconButton>
          )}
        </Box>
      }
      onClick={() => navigate(`/expert/project/${project.id}/confirmation-details`)}
    />
  );

  return (
    <>
      {card}
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setShowProductTesting(true);
          }}
        >
          Product Feedback Form
        </MenuItem>
      </Menu>
      {showProductTesting && (
        <ProductTestingModal
          projectId={project.id}
          onClose={() => setShowProductTesting(false)}
        />
      )}
    </>
  );
};

export default ExpertProjectCard;
